import type { RenderStatusResponse } from '../../../shared/types';
import { fetchRenderStatus, retryRender, UserFacingError } from './api';
import { clearActiveRenderGroupId } from './storage';

const POLL_INTERVAL_MS = 5_000;

/**
 * Polls GET /api/render/:groupId on a fixed interval and reports snapshots
 * back to the caller until the job reaches a terminal state. A render can
 * run for hours, so this is designed to just keep going quietly — a
 * transient network hiccup talking to *our own* server is treated as
 * "try again next tick" rather than a failure (the render API's own
 * retry/backoff already happens server-side).
 */
export class RenderTracker {
  private readonly groupId: string;
  private readonly onUpdate: (status: RenderStatusResponse | null) => void;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private lastStatus: RenderStatusResponse | null = null;

  constructor(groupId: string, onUpdate: (status: RenderStatusResponse | null) => void) {
    this.groupId = groupId;
    this.onUpdate = onUpdate;
    void this.poll();
  }

  getStatus(): RenderStatusResponse | null {
    return this.lastStatus;
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) clearTimeout(this.timer);
  }

  async retry(): Promise<void> {
    try {
      await retryRender(this.groupId);
    } catch {
      // The next poll tick will just keep reflecting whatever the server
      // actually knows; nothing more useful to do with this failure here.
    }
    this.stopped = false;
    void this.poll();
  }

  private schedule(): void {
    if (this.stopped) return;
    this.timer = setTimeout(() => void this.poll(), POLL_INTERVAL_MS);
  }

  private async poll(): Promise<void> {
    if (this.stopped) return;
    try {
      const status = await fetchRenderStatus(this.groupId);
      this.lastStatus = status;
      this.onUpdate(status);
      if (status.status === 'completed' || status.status === 'failed') {
        this.stop();
        return;
      }
      this.schedule();
    } catch (err) {
      if (err instanceof UserFacingError && err.code === 'not_found') {
        // The server has no memory of this render (e.g. it restarted).
        // Nothing to recover — stop quietly rather than showing an error
        // for a job the user has no way to act on anymore.
        clearActiveRenderGroupId();
        this.lastStatus = null;
        this.onUpdate(null);
        this.stop();
        return;
      }
      this.schedule();
    }
  }
}
